import type { ReactNode } from 'react';
import type { FormOptions, FormResult, FormState } from '@frontend/utils/hooks/useForm';
import { useForm } from '@frontend/utils/hooks/useForm';
import { FormContext } from './formContext';

interface FormProviderProps<T extends FormState> extends FormOptions<T> {
    children: ReactNode | ((renderProps: FormResult<T>) => ReactNode);
    enableDebug?: boolean;
}

/**
 * FormProvider Component
 *
 * This component is a context provider for form-related data and functions. It uses the context API to provide
 * form state and form handlers to its child components.
 *
 * Props:
 * - initialValues: An object that defines the initial values for each field in the form. Each key-value pair in the object
 *   corresponds to a form field and its initial value.
 * - onSubmit: A function that is called when the form is submitted. This function receives the form values as its argument.
 * - validationSchema: A Koi schema that defines the validation rules for the form.
 *
 * Children:
 * - A function that receives the form context (an object with form state and handlers) and returns a React node.
 *   This function-as-children pattern allows you to access the form context in your form components.
 *
 * Example usage:
 *
 * ```tsx
 * <FormProvider
 *     initialValues={{ field1: '' }}
 *     onSubmit={(values) => await myApiService(values)}
 *     validationSchema={myValidationSchema}
 * >
 *     {(form) => (
 *         <Form onReset={form.handleReset} onSubmit={form.handleSubmit}>
 *             <FormGroup
                    isRequired={true}
                    label="Field 1"
                >
                    <TextArea
                        error={form.errors.getMessage('field1')}
                        name="field1" // the name property is used to "register" the field with the FormProvider.
                        onBlur={form.handleBlur}
                        onChange={form.handleChange}
                        value={form.values.field1}
                    />
               </FormGroup>
 *         </Form>
 *     )}
 * </FormProvider>
 * ```
 */
export function FormProvider<T extends FormState>(props: Readonly<FormProviderProps<T>>) {
    const form = useForm(props);

    return (
        <FormContext.Provider value={form}>
            {props.enableDebug ? (
                <>
                    {renderChildren(props.children)}
                    <pre>{JSON.stringify(form, null, 2)}</pre>
                </>
            ) : (
                renderChildren(props.children)
            )}
        </FormContext.Provider>
    );

    function renderChildren(children: typeof props.children) {
        if (typeof children === 'function') {
            return children(form);
        }

        return children;
    }
}
